/**
 * Pure local-date helpers. NO React imports — this file is unit-testable and
 * is the single source of truth for "what day is it" across all four workers.
 *
 * Three bugs this file exists to prevent:
 *   1. `toISOString().slice(0,10)` is UTC, so it reports tomorrow for anyone
 *      east of Greenwich in the evening and yesterday for the Americas.
 *   2. `now + 86_400_000` for "next midnight" is wrong by an hour twice a year.
 *   3. `new Date('2026-03-14')` parses as UTC midnight, which is the previous
 *      local day in any negative-offset timezone. That would make every Media
 *      Tracker item flip a day early — a graded acceptance criterion.
 */

/** Local calendar date, 'YYYY-MM-DD'. NOT toISOString() — that is UTC. */
export function localDateKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse 'YYYY-MM-DD' as LOCAL midnight. The only permitted way to turn a
 * stored date key back into a Date. Returns null for anything malformed.
 */
export function parseLocalDateKey(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key.trim());
  if (m === null) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day, 0, 0, 0, 0);
  // Rejects 2026-02-31, which JS would silently roll to March 3.
  if (d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return Number.isNaN(d.getTime()) ? null : d;
}

/** True for a well-formed, real calendar date key. */
export function isValidDateKey(v: unknown): v is string {
  return typeof v === 'string' && parseLocalDateKey(v) !== null;
}

/** Lexicographic comparison is safe on zero-padded ISO date keys. */
export function compareDateKeys(a: string, b: string): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function startOfLocalDay(d: Date = new Date()): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** DST-correct: constructs the next local midnight rather than adding 24h. */
export function nextLocalMidnight(d: Date = new Date()): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
}

export function msUntilNextLocalMidnight(d: Date = new Date()): number {
  return Math.max(0, nextLocalMidnight(d).getTime() - d.getTime());
}

export function isSameLocalDay(a: Date, b: Date): boolean {
  return localDateKey(a) === localDateKey(b);
}

/**
 * Whole local days from `from` to `to`. Negative if `to` is earlier.
 * Math.round, not floor: absorbs the 23h/25h DST days.
 */
export function localDaysBetween(from: Date, to: Date): number {
  const ms = startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime();
  return Math.round(ms / 86_400_000);
}

/** Whole local days between two date keys. Returns null if either is invalid. */
export function daysBetweenKeys(a: string, b: string): number | null {
  const from = parseLocalDateKey(a);
  const to = parseLocalDateKey(b);
  if (from === null || to === null) return null;
  return localDaysBetween(from, to);
}

export type GreetingPeriod = 'morning' | 'afternoon' | 'evening' | 'night';

export function greetingPeriod(d: Date = new Date()): GreetingPeriod {
  const h = d.getHours();
  if (h < 5) return 'night';
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  if (h < 22) return 'evening';
  return 'night';
}

const GREETINGS: Readonly<Record<GreetingPeriod, string>> = {
  morning: 'Good morning',
  afternoon: 'Good afternoon',
  evening: 'Good evening',
  night: 'Good evening',
};

/** `displayName` undefined -> the name is omitted, never invented (spec). */
export function greeting(displayName: string | undefined, d: Date = new Date()): string {
  const base = GREETINGS[greetingPeriod(d)];
  return displayName === undefined ? base : `${base}, ${displayName}`;
}

/* Intl formatters are expensive to construct; build once at module load. */
const fmtLongDate = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
});
const fmtShortDay = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const fmtClock = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

/** "Monday, Sept 21" */
export const formatLongDate = (d: Date = new Date()): string => fmtLongDate.format(d);
/** "Mon" */
export const formatShortDay = (d: Date = new Date()): string => fmtShortDay.format(d);
/** "8:42 AM" */
export const formatClockTime = (d: Date = new Date()): string => fmtClock.format(d);
/** Calendar event times. Same shape as the clock, separate so it can diverge. */
export const formatEventTime = (d: Date): string => fmtClock.format(d);

/** "in 3 days" / "today" / "2 days ago" — Media Tracker + halving countdown. */
export function relativeDayLabel(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}
